import React, { FormEvent, useCallback, useState } from 'react';
import { Text, TextVariant } from '@naf/text';
import { apiClient, ApiPaths, ApiType } from '@shared/apiClient';
import * as S from './SubmissionForm.styles';

type SubmissionFormProps = { feedId: string; onSubmitSucceed: () => void };

const SubmissionForm = ({ feedId, onSubmitSucceed }: SubmissionFormProps) => {
  const [name, setName] = useState('');
  const [question, setQuestion] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const questionTooLong = question.length > 500;
  const questionTooShort = question.length < 10;

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setIsSubmitting(true);

      try {
        await apiClient.post<any>(ApiType.apimNafNoApi, {
          url: ApiPaths[ApiType.apimNafNoApi].qnafeed.ask(feedId),
          data: { name, question },
        });

        setSubmissionError(null);
        onSubmitSucceed();
      } catch (error) {
        setSubmissionError('Noe gikk galt med innsendingen. Prøv igjen senere.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [name, question, feedId, onSubmitSucceed],
  );

  return (
    <form onSubmit={handleSubmit}>
      <S.Label htmlFor="name">
        <S.LabelText variant={TextVariant.Small}>Navn</S.LabelText>
        <S.NameInput
          type="text"
          name="name"
          placeholder="Ditt navn eller kallenavn..."
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </S.Label>

      <S.Label htmlFor="question">
        <S.LabelText variant={TextVariant.Small}>Spørsmål</S.LabelText>
        <S.QuestionTextArea
          name="question"
          placeholder="Ditt spørsmål..."
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
        />
        <S.LetterCounter $tooLong={questionTooLong} variant={TextVariant.Small}>
          {question.length}/500 tegn
        </S.LetterCounter>
      </S.Label>
      {submissionError && <S.ErrorText variant={TextVariant.Small}>{submissionError}</S.ErrorText>}
      <S.SubmitButton
        color="secondary"
        type="submit"
        disabled={questionTooLong || questionTooShort || name.length < 2}
        isLoading={isSubmitting}
      >
        <Text variant={TextVariant.Button}>Send inn</Text>
        <S.SubmitIcon size={16} />
      </S.SubmitButton>

      <S.CaptionText variant={TextVariant.Caption}>
        Ikke alle spørsmål blir besvart, og useriøse innsendelser vil bli ignorert.
      </S.CaptionText>
    </form>
  );
};

export { SubmissionForm };
